/**
 * SEO Configuration for PinDay
 * Central configuration for all SEO metadata across languages
 */

import type { SEOPageConfig, SchemaOrgConfig, SupportedLanguage } from './types'

export const SITE_URL = 'https://pinday.me'
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`

export const LANGUAGE_LOCALES: Record<SupportedLanguage, string> = {
  pt: 'pt_BR',
  en: 'en_US',
  es: 'es_ES',
}

export const HREFLANG_MAP: Record<SupportedLanguage, string> = {
  pt: 'pt-BR',
  en: 'en',
  es: 'es',
}

/**
 * Schema.org configuration for the organization
 */
export const SCHEMA_ORG_CONFIG: SchemaOrgConfig = {
  organization: {
    name: 'PinDay',
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    description: 'Booking with deposit for photographers, doulas, tattoo artists and date-based professionals',
    sameAs: [
      'https://twitter.com/pinday',
      'https://instagram.com/pinday',
      'https://linkedin.com/company/pinday',
    ],
  },
  softwareApplication: {
    name: 'PinDay',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: {
      price: '0',
      priceCurrency: 'USD',
    },
  },
}

/**
 * SEO metadata for all pages by language
 */
export const SEO_PAGES: Record<string, SEOPageConfig> = {
  home: {
    path: '/',
    priority: 1.0,
    changefreq: 'weekly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay - Reserve datas com cobrança one-off ou recorrente',
        description: 'Envie propostas, cobre one-off com sinal ou recorrente via Stripe e bloqueie datas com políticas claras, Google Calendar e fila de espera Pro+.',
        keywords: ['reserva de datas', 'one-off', 'recorrente via stripe', 'sinal para reservas', 'fila de espera pro'],
        h1: 'Reserve datas com cobrança one-off ou recorrente',
        h2: 'Pagamento, regras e calendário no mesmo fluxo para profissionais que vendem datas',
      },
      en: {
        title: 'PinDay - Reserve dates with one-off or recurring billing',
        description: 'Send proposals, collect one-off deposits or recurring Stripe subscriptions, and block dates with clear policies, Google Calendar, and a Pro+ waitlist.',
        keywords: ['date reservations', 'one-off billing', 'stripe recurring', 'deposit for bookings', 'pro waitlist'],
        h1: 'Reserve dates with one-off or recurring billing',
        h2: 'Payment, rules, and calendar in one flow for professionals who sell dates',
      },
      es: {
        title: 'PinDay - Reserva fechas con cobro one-off o recurrente',
        description: 'Envía propuestas, cobra one-off con seña o recurrente vía Stripe y bloquea fechas con políticas claras, Google Calendar y lista de espera Pro+.',
        keywords: ['reserva de fechas', 'one-off', 'recurrente stripe', 'seña para reservas', 'lista de espera pro'],
        h1: 'Reserva fechas con cobro one-off o recurrente',
        h2: 'Pago, reglas y calendario en el mismo flujo para profesionales que venden fechas',
      },
    },
  },

  pricing: {
    path: '/pricing',
    priority: 0.9,
    changefreq: 'weekly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'Preços - PinDay | Planos para profissionais por data',
        description: 'Planos flexíveis para fotógrafos, doulas e tatuadores. Comece grátis e escale conforme cresce. Pix, cartão, contratos e políticas de cancelamento inclusos.',
        keywords: ['preços pinday', 'planos fotógrafo', 'quanto custa pinday', 'agendamento com sinal preço'],
        h1: 'Planos que crescem com você',
        h2: 'Comece grátis. Escale quando precisar.',
      },
      en: {
        title: 'Pricing - PinDay | Plans for date-based professionals',
        description: 'Flexible plans for photographers, doulas and tattoo artists. Start free and scale as you grow. Card payments, contracts and cancellation policies included.',
        keywords: ['pinday pricing', 'photographer plans', 'booking deposit pricing', 'how much pinday costs'],
        h1: 'Plans that grow with you',
        h2: 'Start free. Scale when you need.',
      },
      es: {
        title: 'Precios - PinDay | Planes para profesionales por fecha',
        description: 'Planes flexibles para fotógrafos, doulas y tatuadores. Empieza gratis y escala mientras creces. Pagos con tarjeta, contratos y políticas de cancelación incluidos.',
        keywords: ['precios pinday', 'planes fotógrafo', 'reserva con depósito precio', 'cuánto cuesta pinday'],
        h1: 'Planes que crecen contigo',
        h2: 'Empieza gratis. Escala cuando lo necesites.',
      },
    },
  },

  'solutions-photographers': {
    path: '/solutions/photographers',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay para Fotógrafos | Sinal por Pix e cartão que bloqueia seu sábado',
        description: 'Receba sinal por Pix ou cartão e bloqueie automaticamente a data no Google Calendar. Contratos, políticas de cancelamento e 3 pacotes em um único link para fotógrafos de casamento e ensaios.',
        keywords: ['sinal fotógrafo por pix', 'reserva de data fotógrafo casamento', 'contrato de ensaio com sinal', 'fotógrafo receber sinal', 'booking fotógrafo'],
        h1: 'Depósito que bloqueia seu sábado como fotógrafo',
        h2: 'Sinal, contrato e calendário em um só link',
      },
      en: {
        title: 'PinDay for Photographers | Deposit that holds your Saturday',
        description: 'Receive deposits via card and automatically block the date on Google Calendar. Contracts, cancellation policies and 3 packages in a single link for wedding and portrait photographers.',
        keywords: ['photography deposit booking', 'wedding photographer retainer', 'photographer booking deposit', 'hold date photographer', 'photographer contract deposit'],
        h1: 'Deposit that holds your Saturday as a photographer',
        h2: 'Retainer, contract and calendar in one link',
      },
      es: {
        title: 'PinDay para Fotógrafos | Seña que reserva tu sábado',
        description: 'Recibe seña por tarjeta y bloquea automáticamente la fecha en Google Calendar. Contratos, políticas de cancelación y 3 paquetes en un solo link para fotógrafos de bodas y sesiones.',
        keywords: ['seña fotógrafo', 'reserva de fecha fotógrafo boda', 'contrato de sesión con seña', 'fotógrafo recibir seña', 'booking fotógrafo'],
        h1: 'Seña que reserva tu sábado como fotógrafo',
        h2: 'Seña, contrato y calendario en un solo link',
      },
    },
  },

  'solutions-doulas': {
    path: '/solutions/doulas',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay para Doulas | Reserva de parto com sinal automático',
        description: 'Receba sinal para reservar a data prevista do parto. Pacotes de pré-natal, parto e pós-parto com contratos e políticas de cancelamento. Bloqueio automático no Google Calendar.',
        keywords: ['sinal para parto doula', 'reserva de parto com doula', 'doula receber sinal', 'contrato doula parto', 'agendamento doula'],
        h1: 'Reserve partos com sinal automático',
        h2: 'Pré-natal, parto e pós-parto em pacotes com contrato',
      },
      en: {
        title: 'PinDay for Doulas | Birth booking with automatic deposit',
        description: 'Receive deposits to book the expected birth date. Prenatal, birth and postpartum packages with contracts and cancellation policies. Automatic Google Calendar blocking.',
        keywords: ['doula deposit booking', 'birth doula retainer', 'doula booking deposit', 'doula contract', 'doula scheduling'],
        h1: 'Book births with automatic deposit',
        h2: 'Prenatal, birth and postpartum packages with contract',
      },
      es: {
        title: 'PinDay para Doulas | Reserva de parto con seña automática',
        description: 'Recibe seña para reservar la fecha prevista del parto. Paquetes de prenatal, parto y posparto con contratos y políticas de cancelación. Bloqueo automático en Google Calendar.',
        keywords: ['seña para parto doula', 'reserva de parto con doula', 'doula recibir seña', 'contrato doula parto', 'agendamiento doula'],
        h1: 'Reserva partos con seña automática',
        h2: 'Prenatal, parto y posparto en paquetes con contrato',
      },
    },
  },

  'solutions-tattoo-artists': {
    path: '/solutions/tattoo-artists',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay para Tatuadores | Sinal que garante sua sessão',
        description: 'Receba sinal por Pix ou cartão antes da sessão de tatuagem. Evite no-shows com políticas de cancelamento claras. Bloqueio automático da agenda.',
        keywords: ['sinal tatuagem', 'tatuador receber sinal', 'reserva sessão tatuagem', 'tatuador agendamento', 'depósito tatuagem'],
        h1: 'Sinal que garante sua sessão de tattoo',
        h2: 'Acabe com no-shows. Receba antes de tatuar.',
      },
      en: {
        title: 'PinDay for Tattoo Artists | Deposit that secures your session',
        description: 'Receive deposits via card before your tattoo session. Avoid no-shows with clear cancellation policies. Automatic calendar blocking.',
        keywords: ['tattoo deposit', 'tattoo artist booking', 'tattoo session deposit', 'tattoo scheduling', 'tattoo retainer'],
        h1: 'Deposit that secures your tattoo session',
        h2: 'End no-shows. Get paid before you ink.',
      },
      es: {
        title: 'PinDay para Tatuadores | Seña que asegura tu sesión',
        description: 'Recibe seña por tarjeta antes de la sesión de tatuaje. Evita no-shows con políticas de cancelación claras. Bloqueo automático de agenda.',
        keywords: ['seña tatuaje', 'tatuador recibir seña', 'reserva sesión tatuaje', 'tatuador agendamiento', 'depósito tatuaje'],
        h1: 'Seña que asegura tu sesión de tattoo',
        h2: 'Termina con los no-shows. Cobra antes de tatuar.',
      },
    },
  },

  'solutions-wedding-planners': {
    path: '/solutions/wedding-planners',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay para Wedding Planners | Sinal que trava a data do casamento',
        description: 'Receba sinal para reservar datas de casamento. Pacotes de assessoria completa, parcial e do dia com contratos profissionais. Calendário sincronizado automaticamente.',
        keywords: ['sinal wedding planner', 'reserva casamento assessoria', 'assessora casamento sinal', 'contrato wedding planner', 'agendamento casamento'],
        h1: 'Sinal que trava a data do casamento',
        h2: 'Assessoria completa, parcial ou do dia com contrato',
      },
      en: {
        title: 'PinDay for Wedding Planners | Deposit that holds the wedding date',
        description: 'Receive deposits to book wedding dates. Full, partial and day-of planning packages with professional contracts. Automatically synced calendar.',
        keywords: ['wedding planner deposit', 'wedding booking retainer', 'wedding planner contract', 'wedding date deposit', 'wedding scheduling'],
        h1: 'Deposit that holds the wedding date',
        h2: 'Full, partial or day-of planning with contract',
      },
      es: {
        title: 'PinDay para Wedding Planners | Seña que reserva la fecha de la boda',
        description: 'Recibe seña para reservar fechas de boda. Paquetes de asesoría completa, parcial y del día con contratos profesionales. Calendario sincronizado automáticamente.',
        keywords: ['seña wedding planner', 'reserva boda asesoría', 'asesora boda seña', 'contrato wedding planner', 'agendamiento boda'],
        h1: 'Seña que reserva la fecha de la boda',
        h2: 'Asesoría completa, parcial o del día con contrato',
      },
    },
  },

  'solutions-personal-organizers': {
    path: '/solutions/personal-organizers',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay para Personal Organizers | Sinal para projetos de organização',
        description: 'Receba sinal para projetos de organização residencial ou comercial. Pacotes por ambiente ou projeto completo com contratos e políticas claras.',
        keywords: ['sinal personal organizer', 'reserva organização', 'personal organizer agendamento', 'contrato personal organizer', 'organização residencial sinal'],
        h1: 'Sinal para projetos de organização',
        h2: 'Por ambiente ou projeto completo com contrato',
      },
      en: {
        title: 'PinDay for Personal Organizers | Deposit for organizing projects',
        description: 'Receive deposits for residential or commercial organizing projects. Per-room or full-project packages with contracts and clear policies.',
        keywords: ['personal organizer deposit', 'organizing booking', 'personal organizer scheduling', 'organizer contract', 'home organizing deposit'],
        h1: 'Deposit for organizing projects',
        h2: 'Per-room or full-project with contract',
      },
      es: {
        title: 'PinDay para Personal Organizers | Seña para proyectos de organización',
        description: 'Recibe seña para proyectos de organización residencial o comercial. Paquetes por ambiente o proyecto completo con contratos y políticas claras.',
        keywords: ['seña personal organizer', 'reserva organización', 'personal organizer agendamiento', 'contrato personal organizer', 'organización residencial seña'],
        h1: 'Seña para proyectos de organización',
        h2: 'Por ambiente o proyecto completo con contrato',
      },
    },
  },

  // Tier 2 Solution Pages - Secondary ICPs

  'solutions-event-planners': {
    path: '/solutions/event-planners',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay para Organizadores de Eventos | Sinal para festas e corporativos',
        description: 'Garanta a data do evento com um sinal. Contratos para festas de aniversário, eventos corporativos e lançamentos. Calendário bloqueado automaticamente.',
        keywords: ['sinal organizador eventos', 'reserva evento corporativo', 'contrato festa aniversário', 'depósito evento', 'agendamento eventos'],
        h1: 'Sinal para festas e eventos corporativos',
        h2: 'Reserva de data garantida com contrato digital',
      },
      en: {
        title: 'PinDay for Event Planners | Deposit for parties and corporate events',
        description: 'Secure the event date with a deposit. Contracts for birthday parties, corporate events and launches. Calendar blocked automatically.',
        keywords: ['event planner deposit', 'corporate event booking', 'birthday party contract', 'event deposit', 'event scheduling'],
        h1: 'Deposit for parties and corporate events',
        h2: 'Guaranteed date reservation with digital contract',
      },
      es: {
        title: 'PinDay para Organizadores de Eventos | Seña para fiestas y corporativos',
        description: 'Asegura la fecha del evento con una seña. Contratos para fiestas de cumpleaños, eventos corporativos y lanzamientos. Calendario bloqueado automáticamente.',
        keywords: ['seña organizador eventos', 'reserva evento corporativo', 'contrato fiesta cumpleaños', 'depósito evento', 'agendamiento eventos'],
        h1: 'Seña para fiestas y eventos corporativos',
        h2: 'Reserva de fecha garantizada con contrato digital',
      },
    },
  },

  'solutions-yoga-instructors': {
    path: '/solutions/yoga-instructors',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay para Instrutores de Yoga | Sinal para aulas e retiros',
        description: 'Garanta a presença dos alunos em aulas privadas ou workshops. Receba antecipado e evite cancelamentos de última hora.',
        keywords: ['sinal aula yoga', 'reserva retiro yoga', 'yoga agendamento', 'workshop yoga sinal', 'instrutor yoga pagamento'],
        h1: 'Sinal para aulas particulares e retiros',
        h2: 'Pagamento antecipado e compromisso do aluno',
      },
      en: {
        title: 'PinDay for Yoga Instructors | Deposit for classes and retreats',
        description: 'Ensure student attendance in private classes or workshops. Get paid upfront and avoid last-minute cancellations.',
        keywords: ['yoga class deposit', 'yoga retreat booking', 'yoga scheduling', 'yoga workshop deposit', 'yoga instructor payment'],
        h1: 'Deposit for private classes and retreats',
        h2: 'Upfront payment and student commitment',
      },
      es: {
        title: 'PinDay para Instructores de Yoga | Seña para clases y retiros',
        description: 'Asegura la asistencia de alumnos en clases privadas o workshops. Cobra por adelantado y evita cancelaciones de última hora.',
        keywords: ['seña clase yoga', 'reserva retiro yoga', 'yoga agendamiento', 'workshop yoga seña', 'instructor yoga pago'],
        h1: 'Seña para clases particulares y retiros',
        h2: 'Pago anticipado y compromiso del alumno',
      },
    },
  },

  'solutions-fitness-coaches': {
    path: '/solutions/fitness-coaches',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay para Personal Trainers | Sinal para consultoria e aulas',
        description: 'Venda consultorias online, avaliações físicas e pacotes de aulas. Receba antes de montar o treino. Anamnese inclusa no agendamento.',
        keywords: ['sinal personal trainer', 'consultoria fitness online', 'avaliação física agendamento', 'personal trainer pagamento', 'treino online sinal'],
        h1: 'Sinal para consultoria e aulas',
        h2: 'Pagamento garantido antes de montar o treino',
      },
      en: {
        title: 'PinDay for Fitness Coaches | Deposit for coaching and training',
        description: 'Sell online coaching, fitness assessments and training packages. Get paid before designing the workout. Health questionnaire included.',
        keywords: ['fitness coach deposit', 'online coaching booking', 'fitness assessment scheduling', 'personal trainer payment', 'online training deposit'],
        h1: 'Deposit for coaching and training',
        h2: 'Guaranteed payment before designing the workout',
      },
      es: {
        title: 'PinDay para Personal Trainers | Seña para consultoría y clases',
        description: 'Vende consultorías online, evaluaciones físicas y paquetes de clases. Cobra antes de armar el entrenamiento. Anamnesis incluida.',
        keywords: ['seña personal trainer', 'consultoría fitness online', 'evaluación física agendamiento', 'personal trainer pago', 'entrenamiento online seña'],
        h1: 'Seña para consultoría y clases',
        h2: 'Pago garantizado antes de armar el entrenamiento',
      },
    },
  },

  'solutions-hair-stylists': {
    path: '/solutions/hair-stylists',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay para Cabeleireiros | Sinal para mechas e procedimentos longos',
        description: 'Não perca horas da sua agenda com faltas. Cobre um sinal para reservar horários de mechas, alisamentos e mega hair.',
        keywords: ['sinal cabeleireiro', 'reserva mechas', 'agendamento alisamento', 'mega hair sinal', 'coloração cabelo sinal'],
        h1: 'Sinal para mechas e procedimentos longos',
        h2: 'Fim dos furos na agenda para serviços complexos',
      },
      en: {
        title: 'PinDay for Hair Stylists | Deposit for highlights and long services',
        description: 'Don\'t lose hours of your schedule to no-shows. Charge a deposit to book highlights, straightening and extensions.',
        keywords: ['hair stylist deposit', 'highlights booking', 'hair straightening scheduling', 'hair extensions deposit', 'hair color deposit'],
        h1: 'Deposit for highlights and long services',
        h2: 'End schedule gaps for complex services',
      },
      es: {
        title: 'PinDay para Estilistas | Seña para mechas y procedimientos largos',
        description: 'No pierdas horas de tu agenda por faltas. Cobra una seña para reservar horarios de mechas, alisados y extensiones.',
        keywords: ['seña estilista', 'reserva mechas', 'agendamiento alisado', 'extensiones cabello seña', 'coloración cabello seña'],
        h1: 'Seña para mechas y procedimientos largos',
        h2: 'Fin de los huecos en agenda para servicios complejos',
      },
    },
  },

  'solutions-nurses': {
    path: '/solutions/nurses',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay para Enfermeiros | Sinal para plantões e procedimentos',
        description: 'Organize sua agenda de home care. Receba antecipado por plantões, curativos e administração de medicamentos.',
        keywords: ['sinal enfermeiro', 'plantão home care', 'agendamento enfermagem', 'curativo domiciliar sinal', 'enfermeiro particular'],
        h1: 'Sinal para plantões e procedimentos',
        h2: 'Segurança financeira para home care',
      },
      en: {
        title: 'PinDay for Nurses | Deposit for shifts and procedures',
        description: 'Organize your home care schedule. Get paid upfront for shifts, dressings and medication administration.',
        keywords: ['nurse deposit', 'home care shift', 'nursing scheduling', 'wound care deposit', 'private nurse'],
        h1: 'Deposit for shifts and procedures',
        h2: 'Financial security for home care',
      },
      es: {
        title: 'PinDay para Enfermeros | Seña para turnos y procedimientos',
        description: 'Organiza tu agenda de cuidados domiciliarios. Cobra por adelantado por turnos, curaciones y administración de medicamentos.',
        keywords: ['seña enfermero', 'turno cuidados domiciliarios', 'agendamiento enfermería', 'curación domiciliaria seña', 'enfermero particular'],
        h1: 'Seña para turnos y procedimientos',
        h2: 'Seguridad financiera para cuidados domiciliarios',
      },
    },
  },

  'solutions-pop-up-decorators': {
    path: '/solutions/pop-up-decorators',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay para Decoradores de Festas | Sinal para pegue-monte e mini tables',
        description: 'Alugue kits de decoração e mini tables com segurança. Contrato de locação e caução integrados. Controle de estoque automático.',
        keywords: ['sinal decoração festa', 'pegue monte sinal', 'mini table aluguel', 'decoração infantil sinal', 'kit festa reserva'],
        h1: 'Sinal para pegue-monte e mini tables',
        h2: 'Locação segura com contrato digital',
      },
      en: {
        title: 'PinDay for Party Decorators | Deposit for grab-and-go and mini tables',
        description: 'Rent decor kits and mini tables securely. Rental contract and security deposit integrated. Automatic inventory control.',
        keywords: ['party decorator deposit', 'grab and go kit', 'mini table rental', 'kids party decor deposit', 'party kit booking'],
        h1: 'Deposit for grab-and-go and mini tables',
        h2: 'Secure rental with digital contract',
      },
      es: {
        title: 'PinDay para Decoradores de Fiestas | Seña para kits y mini mesas',
        description: 'Alquila kits de decoración y mini mesas con seguridad. Contrato de alquiler y depósito integrados. Control de stock automático.',
        keywords: ['seña decoración fiesta', 'kit para llevar seña', 'mini mesa alquiler', 'decoración infantil seña', 'kit fiesta reserva'],
        h1: 'Seña para kits y mini mesas',
        h2: 'Alquiler seguro con contrato digital',
      },
    },
  },

  'solutions-telemedicine-professionals': {
    path: '/solutions/telemedicine-professionals',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay para Profissionais de Saúde | Consultas online e presenciais',
        description: 'Médicos, psicólogos e terapeutas. Receba pela consulta antes do atendimento. Reduza faltas e organize a agenda com consentimento digital.',
        keywords: ['consulta médica online', 'agendamento psicólogo', 'telemedicina pagamento', 'terapia online sinal', 'consulta particular sinal'],
        h1: 'Sinal para consultas online e presenciais',
        h2: 'Fim da inadimplência em consultas particulares',
      },
      en: {
        title: 'PinDay for Health Professionals | Online and in-person visits',
        description: 'Doctors, psychologists and therapists. Get paid for the visit before the appointment. Reduce no-shows with digital consent.',
        keywords: ['online medical consultation', 'psychologist booking', 'telemedicine payment', 'online therapy deposit', 'private consultation deposit'],
        h1: 'Deposit for online and in-person visits',
        h2: 'End non-payment in private practice',
      },
      es: {
        title: 'PinDay para Profesionales de Salud | Consultas online y presenciales',
        description: 'Médicos, psicólogos y terapeutas. Cobra la consulta antes de la atención. Reduce faltas con consentimiento digital.',
        keywords: ['consulta médica online', 'agendamiento psicólogo', 'telemedicina pago', 'terapia online seña', 'consulta particular seña'],
        h1: 'Seña para consultas online y presenciales',
        h2: 'Fin de la morosidad en consultas particulares',
      },
    },
  },

  'solutions-tarot-readers': {
    path: '/solutions/tarot-readers',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay para Oraculistas | Sinal para consultas de tarot e astrologia',
        description: 'Receba antecipado por suas consultas de tarot, numerologia ou astrologia. Organize sua agenda e evite faltas.',
        keywords: ['sinal tarot', 'consulta astrologia agendamento', 'numerologia online', 'oraculista pagamento', 'leitura tarot sinal'],
        h1: 'Sinal para consultas e leituras',
        h2: 'Agenda organizada e sem faltas',
      },
      en: {
        title: 'PinDay for Tarot Readers | Deposit for tarot and astrology readings',
        description: 'Get paid upfront for your tarot, numerology or astrology consultations. Organize your schedule and avoid no-shows.',
        keywords: ['tarot deposit', 'astrology consultation booking', 'numerology online', 'oracle reader payment', 'tarot reading deposit'],
        h1: 'Deposit for consultations and readings',
        h2: 'Organized schedule without no-shows',
      },
      es: {
        title: 'PinDay para Oraculistas | Seña para consultas de tarot y astrología',
        description: 'Cobra por adelantado por tus consultas de tarot, numerología o astrología. Organiza tu agenda y evita faltas.',
        keywords: ['seña tarot', 'consulta astrología agendamiento', 'numerología online', 'oraculista pago', 'lectura tarot seña'],
        h1: 'Seña para consultas y lecturas',
        h2: 'Agenda organizada sin faltas',
      },
    },
  },

  'solutions-reiki-practitioners': {
    path: '/solutions/reiki-practitioners',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      pt: {
        title: 'PinDay para Terapeutas de Reiki | Sinal para sessões de energia',
        description: 'Receba antecipadamente por suas sessões de Reiki, cura energética ou terapias holísticas. Organize sua agenda e evite faltas.',
        keywords: ['sinal reiki', 'sessão energia agendamento', 'terapia holística sinal', 'reiki à distância', 'cura energética pagamento'],
        h1: 'Sinal para sessões de energia',
        h2: 'Sessões garantidas com pagamento antecipado',
      },
      en: {
        title: 'PinDay for Reiki Practitioners | Deposit for energy sessions',
        description: 'Get paid in advance for your Reiki, energy healing or holistic therapy sessions. Organize your schedule and avoid no-shows.',
        keywords: ['reiki deposit', 'energy session booking', 'holistic therapy deposit', 'distance reiki', 'energy healing payment'],
        h1: 'Deposit for energy sessions',
        h2: 'Guaranteed sessions with upfront payment',
      },
      es: {
        title: 'PinDay para Terapeutas de Reiki | Seña para sesiones de energía',
        description: 'Cobra por adelantado por tus sesiones de Reiki, sanación energética o terapias holísticas. Organiza tu agenda y evita faltas.',
        keywords: ['seña reiki', 'sesión energía agendamiento', 'terapia holística seña', 'reiki a distancia', 'sanación energética pago'],
        h1: 'Seña para sesiones de energía',
        h2: 'Sesiones garantizadas con pago anticipado',
      },
    },
  },
}

/**
 * Pages that should NOT be indexed (app routes)
 */
export const NOINDEX_ROUTES = [
  '/app',
  '/dashboard',
  '/login',
  '/signup',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/onboarding',
  '/settings',
  '/proposals',
  '/events',
  '/packages',
  '/metrics',
  '/billing',
]

/**
 * Get SEO config for a specific page
 */
export function getSEOConfig(pageKey: string, language: SupportedLanguage) {
  const page = SEO_PAGES[pageKey]
  if (!page) return null
  
  return {
    ...page.metadata[language],
    path: page.path,
    priority: page.priority,
    changefreq: page.changefreq,
  }
}

/**
 * Generate all hreflang links for a page
 */
export function getHreflangLinks(pageKey: string): Array<{ lang: string; href: string }> {
  const page = SEO_PAGES[pageKey]
  if (!page) return []

  const links: Array<{ lang: string; href: string }> = []
  
  for (const [lang, hreflang] of Object.entries(HREFLANG_MAP)) {
    links.push({
      lang: hreflang,
      href: `${SITE_URL}${page.path}${lang !== 'en' ? `?lang=${lang}` : ''}`,
    })
  }

  // Add x-default pointing to English
  links.push({
    lang: 'x-default',
    href: `${SITE_URL}${page.path}`,
  })

  return links
}

/**
 * Generate SEO config for dynamic blog posts
 */
export function generateBlogPostSEOConfig(
  post: {
    slug: string
    title: string
    description: string
    image: string
    publishedAt: string
    updatedAt?: string
    seo: { metaDescription: string; keywords: string[] }
  },
  language: SupportedLanguage
): Partial<SEOPageConfig> {
  const path = `/blog/${post.slug}`

  return {
    path,
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    metadata: {
      [language]: {
        title: `${post.title} | PinDay Blog`,
        description: post.seo.metaDescription,
        keywords: post.seo.keywords,
        h1: post.title,
        ogImage: post.image,
        lastModified: post.updatedAt || post.publishedAt,
      },
    } as any,
  }
}

/**
 * Get canonical URL for a page
 */
export function getCanonicalUrl(path: string): string {
  // Remove query params for canonical
  const cleanPath = path.split('?')[0]
  return `${SITE_URL}${cleanPath}`
}
